import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Sparkles, Target } from 'lucide-react';
import { Switch } from '../ui/switch';
import { API, getAuthHeaders } from '../../utils/adminAuth';

/**
 * Coaching tips + weekly goal admin.
 *
 * Tips rotate daily and appear in each ambassador's dashboard as a
 * "Coach's Note" card. The weekly goal drives the goal-tracker ring.
 * Both are owner-controlled — change once, every ambassador feels it
 * by the next dashboard load.
 */
export default function CoachingTipsTab() {
  const [tips, setTips] = useState([]);
  const [weeklyGoal, setWeeklyGoal] = useState(5);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [tipsRes, cfgRes] = await Promise.all([
        axios.get(`${API}/admin/coaching-tips`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/business-config`, { headers: getAuthHeaders() }).catch(() => ({ data: {} })),
      ]);
      setTips(tipsRes.data || []);
      setWeeklyGoal(cfgRes.data?.ambassador_weekly_goal || 5);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const saveWeekly = async () => {
    try {
      await axios.put(`${API}/admin/coaching-tips/weekly-goal`, { weekly_goal: parseInt(weeklyGoal) || 5 }, { headers: getAuthHeaders() });
      toast.success('Weekly goal updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const newTip = () => { setEditing(null); setForm({ text: '', is_active: true, sort_order: tips.length }); };

  const editTip = (t) => { setEditing(t); setForm({ text: t.text, is_active: t.is_active, sort_order: t.sort_order || 0 }); };

  const saveTip = async () => {
    if (!form.text.trim()) { toast.error('Tip text required'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/admin/coaching-tips/${editing.id}`, form, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/admin/coaching-tips`, form, { headers: getAuthHeaders() });
      }
      toast.success('Saved');
      setForm(null); setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const delTip = async (t) => {
    if (!window.confirm('Delete this tip?')) return;
    try { await axios.delete(`${API}/admin/coaching-tips/${t.id}`, { headers: getAuthHeaders() }); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div data-testid="coaching-tab" className="space-y-5">
      {/* Weekly Goal */}
      <div className="bg-[#141414] border border-white/5 p-4 sm:p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="weekly-goal-card">
        <div className="flex items-center gap-3">
          <Target className="w-5 h-5 text-[#D4AF37]" />
          <div>
            <p className="text-white text-sm font-sans font-medium">Weekly Bottle Goal</p>
            <p className="text-white/40 text-xs font-sans">Every ambassador's dashboard shows their progress to this target.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input type="number" min="1" max="50" value={weeklyGoal}
            onChange={e => setWeeklyGoal(e.target.value)}
            className="w-20 bg-[#0A0A0A] border border-white/15 px-3 py-2 text-sm text-white text-center"
            data-testid="weekly-goal-input" />
          <span className="text-white/40 text-xs font-sans">bottles/week</span>
          <button onClick={saveWeekly}
            className="px-3 py-2 text-xs uppercase tracking-[0.18em] bg-[#D4AF37] text-black font-medium hover:bg-[#E5C158] transition-colors"
            data-testid="weekly-goal-save">
            Save
          </button>
        </div>
      </div>

      {/* Tips */}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] font-sans flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> Coach's Notes
            </p>
            <h2 className="text-xl text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Daily Tips</h2>
            <p className="text-white/40 text-xs font-sans mt-1">One tip surfaces daily per ambassador (rotated deterministically — everyone sees a different one but it stays put all day).</p>
          </div>
          <button onClick={newTip} data-testid="tip-new-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.18em] font-medium hover:bg-[#E5C158] transition-colors">
            <Plus className="w-3.5 h-3.5" /> New Tip
          </button>
        </div>

        {form && (
          <div className="bg-[#141414] border border-[#D4AF37]/30 p-4 mb-3 space-y-3" data-testid="tip-form">
            <textarea value={form.text} onChange={e => setForm({ ...form, text: e.target.value })}
              placeholder="Pinakamabenta sa Stories yung scent walks. Sample script..."
              rows={3}
              className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm text-white focus:border-[#D4AF37] focus:outline-none resize-none"
              data-testid="tip-text-input" />
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-white/60 font-sans">
                  <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} data-testid="tip-active-toggle" /> Active
                </label>
                <label className="flex items-center gap-2 text-xs text-white/60 font-sans">
                  Sort:
                  <input type="number" value={form.sort_order}
                    onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-16 bg-[#0A0A0A] border border-white/15 px-2 py-1 text-xs text-white" />
                </label>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setForm(null); setEditing(null); }}
                  className="px-4 py-2 text-xs uppercase tracking-[0.18em] border border-white/15 text-white/60 hover:text-white">Cancel</button>
                <button onClick={saveTip}
                  className="px-4 py-2 text-xs uppercase tracking-[0.18em] bg-[#D4AF37] text-black font-medium hover:bg-[#E5C158] transition-colors"
                  data-testid="tip-save-btn">
                  {editing ? 'Update' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-1.5" data-testid="tip-list">
          {loading && <p className="text-white/30 text-sm py-8 text-center font-sans bg-[#141414]">Loading…</p>}
          {!loading && tips.length === 0 && (
            <p className="text-white/30 text-sm py-8 text-center font-sans bg-[#141414]">No tips yet — they auto-seed on first ambassador load.</p>
          )}
          {tips.map(t => (
            <div key={t.id} className={`bg-[#141414] border ${t.is_active ? 'border-white/5' : 'border-white/5 opacity-40'} p-3 flex items-start gap-3`}
              data-testid={`tip-row-${t.id}`}>
              <Sparkles className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0 mt-1" />
              <p className="flex-1 text-white/80 text-sm font-sans">{t.text}</p>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => editTip(t)} className="p-1.5 text-white/40 hover:text-[#D4AF37]" data-testid={`tip-edit-${t.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => delTip(t)} className="p-1.5 text-white/40 hover:text-red-400" data-testid={`tip-del-${t.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
